// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
export const PARENT_RECORD_BINDINGS = {
    // The AI-advisor family — `organization_id` is genuinely the correct tenant
    // key here, and `project` is the only record type for which it is. See the
    // TENANT_KEY_BY_RECORD_TYPE docblock below for why assuming otherwise was
    // the defect that broke `attach_evidence` for every target type.
    project: {
        collection: 'grc_ai_projects',
        idFields: ['_id'],
        tenantKey: 'organization_id',
        idSource: 'projects-service.ts:180 — findOne({ _id, organization_id, deleted_at: { $exists: false } })',
    },
    dpia: {
        collection: 'grc_dpia_records',
        idFields: ['dpia_id'],
        tenantKey: 'org_id',
        idSource: 'dpia-service.ts getById — findOne({ org_id, dpia_id }); insert at :92 writes _id: ulid() beside it',
    },
    dossier_filing: {
        collection: 'grc_dossier_filings',
        idFields: ['filing_id'],
        tenantKey: 'org_id',
        idSource: 'dossier-filing-service.ts getById — findOne({ org_id, filing_id }); insert at :158',
    },
    transfer_assessment: {
        collection: 'grc_transfer_assessments',
        idFields: ['assessment_id'],
        tenantKey: 'org_id',
        idSource: 'transfer-assessment-service.ts getById — findOne({ org_id, assessment_id })',
    },
    vendor: {
        collection: 'grc_vendors',
        idFields: ['vendor_id'],
        tenantKey: 'org_id',
        idSource: 'vendors-service.ts getById — findOne({ org_id, vendor_id }); insert at :130',
    },
    vendor_contract: {
        collection: 'grc_vendor_contracts',
        idFields: ['contract_id'],
        tenantKey: 'org_id',
        idSource: 'contract-service.ts getContractById — findOne({ org_id, contract_id })',
    },
    vendor_acceptance: {
        // `origin/main` once had a stale `grc_vendor_gate_evaluations` here from an
        // unrelated edit; the actual `VendorRiskAcceptance` collection is
        // `grc_vendor_risk_acceptances` (verified in vendor-risk-maturity-service.ts).
        collection: 'grc_vendor_risk_acceptances',
        idFields: ['acceptance_id'],
        tenantKey: 'org_id',
        idSource: 'vendor-risk-maturity-service.ts:669 summarizeRiskAcceptanceForAdvisor — findOne({ org_id, acceptance_id })',
    },
    incident: {
        collection: 'grc_incidents',
        idFields: ['_id'],
        tenantKey: 'org_id',
        idSource: 'incident-service.ts:52 getById — findOne({ org_id, _id })',
    },
    incident_op: {
        collection: 'grc_incident_playbook_activations',
        idFields: ['activation_id'],
        tenantKey: 'org_id',
        idSource: 'playbook-activation-service.ts getById — findOne({ org_id, activation_id }); insert at :79-81',
    },
    breach_notification: {
        collection: 'grc_incident_breach_notifications',
        idFields: ['_id'],
        tenantKey: 'org_id',
        idSource: 'incident-response-maturity-service.ts getBreachNotification — findOne({ org_id, _id })',
    },
    authority_request: {
        collection: 'grc_incident_authority_requests',
        idFields: ['_id'],
        tenantKey: 'org_id',
        idSource: 'incident-response-maturity-service.ts:1345 — findOne({ org_id, _id })',
    },
    supplement_request: {
        collection: 'grc_incident_supplements',
        idFields: ['_id'],
        tenantKey: 'org_id',
        idSource: 'incident-response-maturity-service.ts:1389 — findOne({ org_id, _id })',
    },
    rights_request: {
        collection: 'grc_rights_request_cases',
        idFields: ['case_public_id', '_id'],
        tenantKey: 'org_id',
        idSource: 'rights-requests/repository.ts findByPublicId ({ org_id, case_public_id }) then findById ({ org_id, _id }), in that order, per service.ts:972 summarizeForAdvisor. Both reach production: the route is /cases/:casePublicId, the frontend passes _id.',
    },
    contract: {
        collection: 'grc_vendor_contracts',
        idFields: ['contract_id'],
        tenantKey: 'org_id',
        idSource: 'contract-service.ts getContractById — same collection and id field as vendor_contract',
    },
    task: {
        collection: 'grc_tasks',
        idFields: ['_id'],
        tenantKey: 'org_id',
        idSource: 'task-service.ts:89 getById — findOne({ _id, org_id })',
    },
    data_mapping_activity: {
        collection: 'grc_processing_activities',
        idFields: ['activity_id'],
        tenantKey: 'org_id',
        idSource: 'activities-service.ts getById — findOne({ org_id, activity_id }); insert at :410-411 writes _id: ulid() beside activity_id. Agrees with form-draft-service.ts PARENT_SNAPSHOT_SOURCES.',
    },
    compliance_rule: {
        collection: 'grc_compliance_rules',
        idFields: ['_id'],
        tenantKey: 'org_id',
        idSource: 'compliance-rule-service.ts:81 getById — findOne({ org_id, _id })',
    },
    program: {
        collection: 'grc_maturity_assessments',
        idFields: ['assessment_id'],
        tenantKey: 'org_id',
        idSource: 'maturity-assessment-service.ts:223 getById — findOne({ org_id, assessment_id }); insert at :171-173 writes _id: ulid() beside assessment_id: crypto.randomUUID(). api-client.ts:2851 states the same: "recordId is the assessment id".',
    },
    handbook_assessment: {
        // Sprint 64.167.0 — a DIFFERENT collection from `program`. Verified against
        // `handbook-assessment-service.ts`, which opens
        // `db.collection<HandbookAssessment>('grc_handbook_assessments')`.
        collection: 'grc_handbook_assessments',
        idFields: ['handbook_assessment_id'],
        tenantKey: 'org_id',
        idSource: 'handbook-assessment-service.ts getById — findOne({ org_id, handbook_assessment_id })',
    },
    /**
     * Sprint 62.20.0 — the regulatory classification subject profile.
     *
     * All four facts stated, and `idFields` VERIFIED against the module's own
     * read path rather than assumed: `classification-service.ts` `getProfile`
     * does `this.profiles.findOne({ org_id: orgId, _id: profileId })`, where
     * `this.profiles` is `db.collection(CLASSIFICATION_SUBJECT_PROFILE_COLLECTION)`
     * — the shared constant whose value is `grc_classification_subject_profiles`.
     * It is `_id` and NOT a business id: the profile has a `subject_id`, which is
     * the CANONICAL RECORD's business id (a registry item's or a system's) and is
     * not this record's own key. `summarizeForAdvisor` is called with the profile
     * id the route receives on `/profiles/:profileId/advisor-summary`, which is
     * the same value `getProfile` is called with, so `parent_record.id` and the
     * loader's filter agree.
     *
     * This is the 64.122.0 discipline applied at the point of adding a member:
     * 15 of 35 types were read on a business id while the snapshot loader
     * filtered `{_id}`, and every one of those locks was silently dead. Naming
     * the wrong field here would have been invisible in exactly the same way.
     */
    classification_subject_profile: {
        collection: 'grc_classification_subject_profiles',
        idFields: ['_id'],
        tenantKey: 'org_id',
        idSource: 'classification-service.ts getProfile — findOne({ org_id, _id: profileId })',
    },
    governance_document: {
        collection: 'grc_governance_documents',
        idFields: ['document_id'],
        tenantKey: 'org_id',
        idSource: 'program-governance-maturity-service.ts:1332 summarizeGovernanceDocumentForAdvisor — findOne({ org_id, document_id })',
    },
    remediation_plan: {
        // Sprint 64.122.0 — was `grc_remediation_plans`, a string that occurred
        // ONCE in all of `src/`: its own declaration here. No service opened it,
        // `db/indexes.ts` did not index it, the seeder did not write it. The
        // loader opened an empty collection, so this record type's lock could not
        // fire for any status value whatsoever — which is why the reverted
        // delivery's choice between `completed` and `verified` was a choice
        // between two dead values.
        collection: 'grc_governance_remediation_plans',
        idFields: ['plan_id'],
        tenantKey: 'org_id',
        // Sprint 65.8.0 — re-measured, and NOTHING in this binding changed.
        // Widening the plan's anchor replaced `gap_id: string` with a required
        // discriminated `source`. That is not an identity field, not the tenant key
        // and not the collection, so the three terms this binding is joined on are
        // untouched and Forseti's record-lock gate reads the record exactly as
        // before. The citation below is re-measured rather than left as it was,
        // because a stale line reference inside a correct enumeration is the shape
        // 64.122.0 was written to stop.
        idSource: "program-governance-maturity-service.ts:136 opens db.collection<StoredRemediationPlan>('grc_governance_remediation_plans') — the READ-side type, since a pre-65.8.0 row the 65.8.0 migration could not classify carries no `source`; :1579 reads findOne({ org_id, plan_id }). Indexed at db/indexes.ts:4493, seeded by the cross-industry sample-data package (packs/sample-data/cross-industry/program_governance.json; sample-data-service.ts:553 until Sprint 63.37.0).",
    },
    cross_border_filing: {
        collection: 'grc_cross_border_filings',
        idFields: ['filing_id'],
        tenantKey: 'org_id',
        idSource: 'cross-border-filing-service.ts getById — findOne({ org_id, filing_id }); insert at :122-124',
    },
    // ── Sprint 62.10.0 — the 62.x sector-domain track ─────────────
    // Each collection below was taken from the `*_COLLECTION` constant in the
    // domain's own shared type file, never retyped: `ai-governance.ts:790`,
    // `public-data.ts:390`, `payments-evidence.ts:446`, `platform-evidence.ts:361`,
    // `manufacturing.ts:{52,53,58,61}`, `logistics.ts:{89,90}`. All six domains
    // key on `org_id`; each domain's shared document type declares it second.
    ai_system_profile: {
        collection: 'grc_ai_system_profiles',
        idFields: ['_id'],
        tenantKey: 'org_id',
        idSource: 'ai-governance-service.ts getProfile — findOne({ org_id, _id }). The route param is named `profileId` and the page passes it, but the value IS the _id; measured rather than inferred from the name.',
    },
    open_data_release: {
        collection: 'grc_open_data_releases',
        idFields: ['_id'],
        tenantKey: 'org_id',
        idSource: 'public-data-service.ts getRelease — findOne({ org_id, _id })',
    },
    retention_reconciliation: {
        collection: 'grc_retention_reconciliations',
        idFields: ['_id'],
        tenantKey: 'org_id',
        idSource: 'retention-reconciliation-service.ts getReconciliation — findOne({ org_id, _id })',
    },
    platform_correspondence: {
        collection: 'grc_platform_authority_correspondence',
        idFields: ['_id'],
        tenantKey: 'org_id',
        idSource: 'authority-correspondence-service.ts get — findOne({ org_id, _id })',
    },
    conformity_record: {
        collection: 'grc_manufacturing_conformity_records',
        idFields: ['_id'],
        tenantKey: 'org_id',
        idSource: 'conformity-service.ts:592 getRecord — findOne(idFilter(orgId, recordId)); manufacturing-common.ts:291 returns { org_id, _id }',
    },
    capa_record: {
        collection: 'grc_manufacturing_capa_records',
        idFields: ['_id'],
        tenantKey: 'org_id',
        idSource: 'capa-recall-service.ts:477 getRecord — findOne(idFilter(orgId, recordId)) → { org_id, _id }',
    },
    epr_submission: {
        collection: 'grc_manufacturing_epr_submissions',
        idFields: ['_id'],
        tenantKey: 'org_id',
        idSource: 'epr-service.ts requireSubmission — findOne(idFilter(orgId, submissionId)) → { org_id, _id }',
    },
    remote_access_grant: {
        collection: 'grc_manufacturing_remote_access_grants',
        idFields: ['_id'],
        tenantKey: 'org_id',
        idSource: 'ot-service.ts getGrant — findOne(idFilter(orgId, grantId)) → { org_id, _id }',
    },
    parcel_claim: {
        collection: 'grc_logistics_claims',
        idFields: ['_id'],
        tenantKey: 'org_id',
        idSource: 'claim-service.ts:319 getClaim — findOne({ org_id, _id })',
    },
    postal_authorisation: {
        collection: 'grc_logistics_authorisations',
        idFields: ['_id'],
        tenantKey: 'org_id',
        idSource: 'authorisation-service.ts:365 getAuthorisation — findOne({ org_id, _id })',
    },
    // The three the first decision-permission census missed. `logistics.ts:83`,
    // `trust-evidence.ts:{856,854}`. These three are not declared in
    // `db/indexes.ts` at all — they are indexed by `migrations/trust-evidence-init.ts`,
    // whose docblock names the compound keys `(org_id, identity_profile_id, sequence)`
    // and `(org_id, provider_id, serial_reference)`. Two independent sources agree
    // with the document types, which is the standard applied to the other 32 rows.
    customs_record: {
        collection: 'grc_logistics_customs_records',
        idFields: ['_id'],
        tenantKey: 'org_id',
        idSource: 'customs-service.ts getCustomsRecord — findOne(idFilter(orgId, id)); logistics-common.ts:447 returns { org_id, _id }',
    },
    electronic_submission: {
        collection: 'grc_electronic_submissions',
        idFields: ['_id'],
        tenantKey: 'org_id',
        idSource: 'transaction-service.ts getSubmission — findOne({ org_id, _id })',
    },
    trust_credential: {
        collection: 'grc_trust_credentials',
        idFields: ['_id'],
        tenantKey: 'org_id',
        idSource: 'trust-service.ts getCredential — findOne({ org_id, _id })',
    },
    // ── Sprint 63.22.0 (OD-A5) — the ten 62.13+ registers with a detail view ──
    // Measured at E0, per the 64.122.0 discipline: route → the service getter the
    // detail GET calls → its `findOne` → the collection constant it was opened
    // with. Every collection is the shared `*_COLLECTION` constant the service
    // imports, never retyped. All ten read on `_id` beside `org_id`; none has a
    // business id, so `BUSINESS_ID_TYPES` in `parent-record-bindings.test.ts` is
    // unchanged at fifteen and its complement moves 21 → 31.
    enforcement_case: {
        collection: 'grc_enforcement_cases',
        idFields: ['_id'],
        tenantKey: 'org_id',
        idSource: 'penalty-exposure/enforcement-case-service.ts getById — this.cases.findOne({ org_id, _id }); GET /penalty-exposure/cases/:id',
    },
    authority_direction: {
        collection: 'grc_authority_directions',
        idFields: ['_id'],
        tenantKey: 'org_id',
        idSource: 'authority-operations/direction-service.ts getById — this.directions.findOne({ org_id, _id }); GET /authority-operations/directions/:id',
    },
    regulated_account: {
        collection: 'grc_regulated_accounts',
        idFields: ['_id'],
        tenantKey: 'org_id',
        idSource: 'authority-operations/regulated-account-service.ts getById — this.accounts.findOne({ org_id, _id }); GET /authority-operations/accounts/:id',
    },
    regulatory_placeholder: {
        collection: 'grc_regulatory_placeholders',
        idFields: ['_id'],
        tenantKey: 'org_id',
        idSource: 'regulatory-placeholders/placeholder-service.ts getById — this.records.findOne({ org_id, _id }); GET /regulatory-placeholders/:id',
    },
    sector_licence: {
        collection: 'grc_sector_licences',
        idFields: ['_id'],
        tenantKey: 'org_id',
        idSource: 'sector-licensing/licence-service.ts getById — this.licences.findOne({ org_id, _id }); GET /sector-licensing/licences/:id',
    },
    credit_information_supply: {
        collection: 'grc_credit_information_supplies',
        idFields: ['_id'],
        tenantKey: 'org_id',
        idSource: 'credit-information-supply/supply-service.ts getById → requireSupply — this.supplies.findOne({ org_id, _id }); GET /credit-information-supply/supplies/:supplyId',
    },
    data_service_provider: {
        collection: 'grc_data_service_provider_records',
        idFields: ['_id'],
        tenantKey: 'org_id',
        idSource: 'data-services/provider-service.ts getById → getRecord — this.records.findOne({ org_id, _id }); GET /data-services/providers/:providerId',
    },
    regulatory_disclosure: {
        collection: 'grc_regulatory_disclosures',
        idFields: ['_id'],
        tenantKey: 'org_id',
        idSource: 'investor-protection/regulatory-disclosure-service.ts getById — this.records.findOne({ org_id, _id }); GET /investor-protection/disclosures/:id',
    },
    issued_record: {
        collection: 'grc_issued_records',
        idFields: ['_id'],
        tenantKey: 'org_id',
        idSource: 'issued-records/issued-record-service.ts getRecord → findRecord — this.records.findOne({ org_id, _id }); GET /issued-records/records/:id',
    },
    publication: {
        collection: 'grc_publications',
        idFields: ['_id'],
        tenantKey: 'org_id',
        idSource: 'publishing/publication-service.ts getById — this.publications.findOne({ org_id, _id }); GET /publishing/publications/:id',
    },
};
/** The id field(s) `ParentRecordRef.id` may be stored in, per record type. */
export const ID_FIELDS_BY_RECORD_TYPE = Object.fromEntries(Object.keys(PARENT_RECORD_BINDINGS).map((t) => [
    t,
    PARENT_RECORD_BINDINGS[t].idFields,
]));
export const COLLECTION_BY_RECORD_TYPE = Object.fromEntries(Object.keys(PARENT_RECORD_BINDINGS).map((t) => [
    t,
    PARENT_RECORD_BINDINGS[t].collection,
]));
/**
 * Sprint 62.10.0 — which tenant key each record type's collection is keyed on.
 *
 * ## Why this map has to exist
 *
 * Because assuming it was the defect. `attach-evidence.ts` ran its
 * tenant-isolation pre-check as
 * `findOne({ _id, organization_id: ctx.organization_id })` over the collections
 * above — and every GRC collection here keys on **`org_id`**. A filter naming a
 * field no document declares cannot match, so the pre-check failed for **every
 * legitimate target**, the handler returned `target_not_found`, and it emitted
 * an `attach_evidence_cross_tenant_attempt` audit row — a security accusation —
 * against an honest user doing the one thing the tool exists for. Shipped, and
 * present on `origin/main`.
 *
 * That is Sprint 64.6.0's defect 1 recurring in a sibling handler in the same
 * directory: *"the convention of the caller was applied to the callee."*
 * `organization_id` **is** correct — for the AI-advisor family
 * (`grc_ai_projects`, `grc_ai_chat_sessions`, `grc_ai_workflows`,
 * `grc_evidence_records`), which is where both handlers were written.
 *
 * So the fix is not a normalisation. A blanket swap to `org_id` would break
 * `project`, whose collection genuinely is an AI-family one. The split is real,
 * and the only safe form of it is **declared per collection and verified against
 * the source** — which is also why this is a `Record<ParentRecordType, …>`: a
 * new record type cannot be added without stating its tenant key.
 *
 * Each value was verified against that collection's index declaration in
 * `src/backend/src/db/indexes.ts` and its shared document type. Nothing here is
 * inferred from a neighbour (R-50; G-76).
 *
 * Sprint 64.122.0 — the values now live in `PARENT_RECORD_BINDINGS` above and
 * this map is derived from them. Not a relocation for tidiness: a lock fires
 * only if the collection, the tenant key AND the id field agree, and while the
 * first two were declared here and the third was assumed to be `_id` in the
 * loader, nothing could notice that they disagreed. Edit the binding, not this.
 */
export const TENANT_KEY_BY_RECORD_TYPE = Object.fromEntries(Object.keys(PARENT_RECORD_BINDINGS).map((t) => [
    t,
    PARENT_RECORD_BINDINGS[t].tenantKey,
]));
//# sourceMappingURL=parent-record-collections.js.map